import { ClientAuth } from './ClientAuth';
import { generateRandomHash } from '@/utils';

type ContentHistory = {
  duration: number;
  totalDuration: number;
};

type Config = {
  id: string;
  language: string;
  videoLanguage: string;
};

export class Device {
  public static config: Config = {
    id: '',
    language: 'en',
    videoLanguage: 'en',
  };
  public static timezone: number = new Date().getTimezoneOffset() * -60;
  public static isInstalled: boolean = !!localStorage.getItem('bbsw:installed');

  public static loadConfig(): void {
    const config = localStorage.getItem('bbsw:config');
    if (config) {
      this.config = JSON.parse(config);
    } else {
      this.config.id = generateRandomHash(64);

      const language = navigator.language.toLowerCase();

      if (language.includes('zh-hans')) {
        this.config.language = 'zh';
        this.config.videoLanguage = 'zh';
      } else if (language.includes('en')) {
        this.config.language = 'en';
        this.config.videoLanguage = 'en';
      } else if (language.includes('ko')) {
        this.config.language = 'ko';
        this.config.videoLanguage = 'ko';
      } else if (language.includes('ja')) {
        this.config.language = 'ja';
        this.config.videoLanguage = 'ja';
      } else if (language.includes('es')) {
        this.config.language = 'es';
        this.config.videoLanguage = 'es';
      } else if (language.includes('id')) {
        this.config.language = 'id';
        this.config.videoLanguage = 'id';
      } else if (language.includes('pt')) {
        this.config.language = 'pt';
        this.config.videoLanguage = 'pt';
      } else {
        this.config.language = 'en';
        this.config.videoLanguage = 'en';
      }

      localStorage.setItem('bbsw:config', JSON.stringify(this.config));
    }
  }

  public static get uuid(): string {
    const uuid = localStorage.getItem('bbsw:uuid');

    if (!uuid) {
      const newUuid = generateRandomHash(32);
      localStorage.setItem('bbsw:uuid', newUuid);
      return newUuid;
    }

    return uuid;
  }

  public static get device(): string {
    if (this.isTizen) {
      return 'samsung';
    } else if (this.isWebOS) {
      return 'lg';
    } else {
      return 'browser';
    }
  }

  public static get variant(): string {
    if (this.isWebOS) {
      return 'tv';
    } else if (this.isTizen) {
      return 'tv';
    } else {
      return 'browser';
    }
  }

  public static get os(): string {
    if (this.isTizen) {
      return 'tizen';
    } else if (this.isWebOS) {
      return 'webos';
    } else {
      return 'web';
    }
  }

  public static get osVersion(): string {
    if (this.isTizen) {
      return '0.0.0';
    } else if (this.isWebOS) {
      return '0.0.0';
    } else {
      return '0.0.0';
    }
  }

  public static get language(): string {
    return this.config.language;
  }

  public static get country(): string {
    const lang = navigator.language || (navigator.languages && navigator.languages[0]) || 'en-US';
    const countryMatch = lang.match(/-([A-Z]{2})$/i);
    return countryMatch ? countryMatch[1].toUpperCase() : 'US';
  }

  public static set language(language: string) {
    this.config.language = language;
    localStorage.setItem('bbsw:config', JSON.stringify(this.config));
  }

  public static get videoLanguage(): string {
    return this.config.videoLanguage;
  }

  public static set videoLanguage(language: string) {
    this.config.videoLanguage = language;
    localStorage.setItem('bbsw:config', JSON.stringify(this.config));
  }

  public static getHistory(id: number): ContentHistory | undefined {
    if (ClientAuth.user) {
      const history = localStorage.getItem(`bbsw:${ClientAuth.user.id}:histories:${id}`);
      if (history) {
        return JSON.parse(history);
      }
    } else {
      const history = localStorage.getItem(`bbsw:histories:${id}`);
      if (history) {
        return JSON.parse(history);
      }
    }

    return undefined;
  }

  public static setHistory(id: number, history: ContentHistory): void {
    if (history.duration && history.totalDuration) {
      if (ClientAuth.user) {
        if (history.duration >= 10 && history.duration <= history.totalDuration - 10) {
          localStorage.setItem(`bbsw:${ClientAuth.user.id}:histories:${id}`, JSON.stringify(history));
        } else {
          localStorage.removeItem(`bbsw:${ClientAuth.user.id}:histories:${id}`);
        }
      } else {
        if (history.duration >= 10 && history.duration <= history.totalDuration - 10) {
          localStorage.setItem(`bbsw:histories:${id}`, JSON.stringify(history));
        } else {
          localStorage.removeItem(`bbsw:histories:${id}`);
        }
      }
    }
  }

  public static get utm_source(): 'bbsworld.samsung' | 'bbsworld.lg' | 'bbsworld.web' {
    if (this.isWebOS) {
      return 'bbsworld.lg';
    } else if (this.isTizen) {
      return 'bbsworld.samsung';
    } else {
      return 'bbsworld.web';
    }
  }

  public static get origin(): string {
    return window.location.origin;
  }

  public static get isTizen(): boolean {
    return navigator.userAgent.includes('Tizen');
  }

  public static get isWebOS(): boolean {
    return navigator.userAgent.includes('WebOS') || navigator.userAgent.includes('Web0S');
  }

  public static close(): void {
    if (this.isWebOS) {
      const webOS = (window as any).webOS;
      if (webOS && webOS.platformBack) {
        webOS.platformBack();
      } else {
        window.close();
      }
    } else if (this.isTizen) {
      const tizen = (window as any).tizen;
      if (tizen) {
        try {
          tizen.application.getCurrentApplication().exit();
        } catch {
          window.close();
        }
      } else {
        window.close();
      }
    } else {
      window.close();
    }
  }
}
